// https://github.com/photolane/parabo-next/blob/e52242f29c111d82078a85a1c6dc252fc6e761c0/_templates/product.html
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import shuffle from 'utils/shuffle';
import queryString from 'utils/queryString';
import { Alert, Button, Section, Container, Grid, Typography, Card } from 'components/base';
import {
  Accordion,
  CustomerReview,
  PrismicHtmlContent,
  PrismicTextContent,
  ProductCard,
  ProductListCarousel,
  ProductDetailCarousel,
} from 'components/rich';
import useDataContext from 'data/context/data';
import { formatPrice } from 'utils/format';
import useCartContext from 'ecommerce/context/cart';
import ProductConfiguration, { prepareBundleData } from 'ecommerce/components/productConfiguration';
import useEditorContext from 'editor/context/editorContext';

import { EditorPageLink, CartPageLink, ProductDetailLink } from '../../components/pageLinks';
import { Head, Layout } from '../../components/layout';

import { MIGHT_LIKE_PRODUCTS } from '../../specifications/lists';
import tracker from '../../lib/tracker'

/**
 * Product has many skuProducts. In case of bundle, there is also secondaryProduct.
 * By default is secondaryProductIndex as -1 - that means "Print Only" version
 */
const ProductDetailPage = props => {
  const cartContext = useCartContext();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();

  const product = dataContext.getProduct(props.productId);
  const [skuProduct, setSkuProduct] = useState(product.options[0]);

  /**
   * Configuration { product, primaryProductData, secondaryProductData, price, bundle }
   */
  const [configuration, setConfiguration] = useState({});

  const [mightLikeProducts, setMightLikeProducts] = useState([]);

  useEffect(() => {
    setSkuProduct(product.options[0]);
    setConfiguration({});

    const allMightLikeProducts = dataContext.getProducts(
      MIGHT_LIKE_PRODUCTS.filter(id => id !== product.id),
    );
    setMightLikeProducts(shuffle(allMightLikeProducts).slice(0, 4));
  }, [product]);

  useEffect(() => {
    tracker.productView(skuProduct);
  }, [skuProduct])

  const handleOnAddToCartClick = () => {
    cartContext.addItem(
      configuration.product,
      configuration.product.version,
      configuration.primaryProductData,
      false,
      prepareBundleData(dataContext, configuration),
    );
  };

  const handleOnStartEditorClick = () => {
    if (configuration.bundle || configuration.upsell) {
      editorContext.createItem(
        configuration.product.id,
        configuration.product.version,
        prepareBundleData(dataContext, configuration),
      );
    } else {
      editorContext.removeBundle(configuration.product.id);
    }
  };

  const handleOnChangeConfiguration = data => {
    if (data.product) {
      setSkuProduct(data.product);
    }
    setConfiguration(data);
  };

  return (
    <React.Fragment key={product.id}>
      <Head
        title={props.content.caption}
        keywords={props.content.seoKeywords}
        description={props.content.shortDescription}
        image={props.content.seoUrl}
      />

      <Layout>
        <Section className="section-product">
          <Container>
            <Grid.Row>
              <Grid.Col xs={12} lg={7} className="pr-lg-2">
                <ProductDetailCarousel images={product.images} rounded />
              </Grid.Col>

              <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-data">
                <Typography.H1 className="mb-3">{props.content.caption}</Typography.H1>

                <div className="list-styled mb-4">
                  <PrismicHtmlContent content={props.content.description} />
                </div>

                {skuProduct ? (
                  <>
                    <div>
                      <ProductConfiguration
                        product={product}
                        skuProduct={skuProduct}
                        onChange={handleOnChangeConfiguration}
                        hidden={product.config.hideProductConfiguration}
                        defaultPrintOnly={product.config.defaultPrintOnly ?? false}
                        hidePrintOnly={product.config.hidePrintOnly ?? false}
                        showSkuDescription={product.config.showSkuDescription}
                        labels={product.config.labels}
                      />
                      <Typography.H6>
                        PRICE{product.config.showSkuPriceStartingAt && ' STARTING AT'}
                      </Typography.H6>
                      <Typography.H3 className="mb-3">
                        {formatPrice(configuration.price, product.currency, true)}
                      </Typography.H3>
                    </div>
                    {skuProduct.display ? (
                      <CartPageLink>
                        <Button type="primary" block onClick={handleOnAddToCartClick}>
                          Add to Cart
                        </Button>
                      </CartPageLink>
                    ) : (
                      <>
                        {product.config.continueButtonUrl ? (
                          <Link
                            href={queryString.addParams(product.config.continueButtonUrl, {
                              product: product.id,
                              ...configuration.config,
                            })}
                            passHref
                            legacyBehavior
                          >
                            <Button type="primary" block onClick={handleOnStartEditorClick}>
                              Continue
                            </Button>
                          </Link>
                        ) : (
                          <EditorPageLink
                            productId={skuProduct.id}
                            version={skuProduct.version}
                            quantity={configuration.quantity}
                          >
                            <Button type="primary" block onClick={handleOnStartEditorClick}>
                              Continue
                            </Button>
                          </EditorPageLink>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Alert type="danger" className="text-center">
                    Sorry, this product is not available.
                  </Alert>
                )}
              </Grid.Col>
            </Grid.Row>

            <Grid.Row className="mt-2">
              <Grid.Col xs={12} lg={7} className="pr-lg-2">
                {props.content.spec.length > 0 && (
                  <Card className="mb-1">
                    <Card.Body className="text-body">
                      <PrismicHtmlContent content={props.content.spec} />
                    </Card.Body>
                  </Card>
                )}
                {props.content.faq && (
                  <Accordion title="FAQ">
                    {props.content.faq.items.map(faq => (
                      <Accordion.Item key={faq.faq_caption} title={faq.faq_caption}>
                        <PrismicHtmlContent content={faq.faq_description} />
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </Grid.Col>

              <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-data">
                {props.content.testimonial && (
                  <CustomerReview className="mt-2 mt-lg-0">
                    <Typography.H3 className="customer-review-text">
                      <PrismicTextContent content={props.content.testimonial.quote} />
                    </Typography.H3>
                    <CustomerReview.Customer>
                      <Typography.Span>
                        <PrismicTextContent content={props.content.testimonial.author} />
                      </Typography.Span>
                    </CustomerReview.Customer>
                  </CustomerReview>
                )}
              </Grid.Col>
            </Grid.Row>
          </Container>
        </Section>

        {mightLikeProducts.length > 0 && (
          <Section>
            <Container>
              <Typography.H3 className="section-title">YOU MIGHT LIKE</Typography.H3>
              <ProductListCarousel
                products={mightLikeProducts}
                className="mb-2"
                renderItem={mightLikeProduct => (
                  <ProductDetailLink productId={mightLikeProduct.id}>
                    <ProductCard asLink product={mightLikeProduct} />
                  </ProductDetailLink>
                )}
              />
            </Container>
          </Section>
        )}

        {props.content.extraDescription && (
          <Section>
            <Container className="my-5">
              <PrismicHtmlContent content={props.content.extraDescription} />
            </Container>
          </Section>
        )}
      </Layout>
    </React.Fragment>
  );
};

ProductDetailPage.getInitialProps = async () => {
  throw new Error('Cannot access this page directly. See /[...params] page');
};

ProductDetailPage.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object,
};

export default ProductDetailPage;
